import {
  Policy as PolicyContract,
  PolicyFileVersion as PolicyFileVersionSchema,
  PolicyFileUpload,
  PolicyList as PolicyListContract,
  OrgJurisdiction as JurisdictionSchema,
} from "@repo/shared/policyContracts";
/**
 * Policies and regulatory updates for the Regulation section.
 *
 * Same idiom as `state/orgProfile.ts`: per-org cached atoms over the
 * workspace server's `/api/amoreg/policies` and
 * `/api/amoreg/regulatory-updates`, which forward to the domain API
 * (`apps/api`) where the database work and the tenant guard live. Nothing
 * here decides who may see what, and no storage URL ever reaches this layer —
 * a policy is downloaded through the server, not from the blob origin.
 *
 * @module state/policies
 */
import { useAtomValue } from "@effect/atom-react";
import * as Effect from "effect/Effect";
import * as Schema from "effect/Schema";
import { AsyncResult, Atom } from "effect/unstable/reactivity";
import { useCallback, useEffect, useMemo, useState } from "react";

import { complianceQueryAtom } from "./complianceLive";
import { resolvePrimaryEnvironmentHttpUrl } from "../environments/primary/target";
import { appAtomRegistry } from "../rpc/atomRegistry";
import type { OrgProfileUnavailableReason } from "./orgProfile";
import { fetchComplianceJson as fetchJson } from "./complianceFetch";
import { domainApiReadPath } from "@repo/shared/domainApiRoutes";

/** A superseded file: downloadable by version number, never by storage URL. */
export type PolicyFileVersion = typeof PolicyFileVersionSchema.Type;

const PolicySchema = Schema.Struct({
  ...PolicyContract.fields,
  // Older workspace servers omit these fields; present values follow the current API contract.
  title: Schema.optionalKey(PolicyContract.fields.title),
  version: Schema.optionalKey(PolicyContract.fields.version),
  versionNote: Schema.optionalKey(PolicyContract.fields.versionNote),
  versionUploadedAt: Schema.optionalKey(PolicyContract.fields.versionUploadedAt),
  versionUploadedByEmail: Schema.optionalKey(PolicyContract.fields.versionUploadedByEmail),
  fileVersions: Schema.optionalKey(PolicyContract.fields.fileVersions),
  documentKind: Schema.optionalKey(PolicyContract.fields.documentKind),
  jurisdictionSource: Schema.optionalKey(PolicyContract.fields.jurisdictionSource),
});
export type Policy = typeof PolicySchema.Type;

/**
 * What to call a policy: its governance title, else the filename without
 * the ".pdf" — "Group AML Framework.pdf" is a file; the policy is not.
 */
export const policyDisplayName = (policy: Pick<Policy, "filename" | "title">): string =>
  policy.title?.trim() ? policy.title.trim() : policy.filename.replace(/\.pdf$/i, "");

/**
 * A file in the organisation's knowledge base (the Knowledge page): reference
 * material eve grounds answers in, not a policy or procedure the firm is
 * accountable for. The existing `reference` flag — so the documents eve files
 * as "knowledge" land on the same page as the ones people upload there.
 */
export const isKnowledgeDocument = (policy: Pick<Policy, "reference">): boolean =>
  policy.reference === true;

/** The version number of the file currently in force. */
export const policyVersion = (policy: Pick<Policy, "version">): number => policy.version ?? 1;

export const formatBytes = (bytes: number): string =>
  bytes >= 1024 * 1024
    ? `${(bytes / (1024 * 1024)).toFixed(1)} MB`
    : `${Math.max(1, Math.round(bytes / 1024))} KB`;
/** Superseded files, oldest first; empty for a never-replaced policy. */
export const policyFileVersions = (
  policy: Pick<Policy, "fileVersions">,
): ReadonlyArray<PolicyFileVersion> => policy.fileVersions ?? [];

export type PolicyJurisdiction = typeof JurisdictionSchema.Type;

const PolicyListSchema = Schema.Struct({
  ...PolicyListContract.fields,
  policies: Schema.Array(PolicySchema),
});
export type PolicyListSnapshot = typeof PolicyListSchema.Type;

export const PublicationKindSchema = Schema.Literals([
  "consultation",
  "guidance",
  "rule",
  "enforcement",
  "notice",
  "other",
]);
export const PublicationStageSchema = Schema.Literals([
  "proposed",
  "consultation",
  "adopted",
  "in_force",
  "repealed",
  "unknown",
]);
export const PublicationApplicabilitySchema = Schema.Literals(["needs_review", "relevant", "not_applicable"]);
export type PublicationKind = typeof PublicationKindSchema.Type;
export type PublicationStage = typeof PublicationStageSchema.Type;
export type PublicationApplicability = typeof PublicationApplicabilitySchema.Type;

const RegulatoryUpdateSchema = Schema.Struct({
  id: Schema.String,
  jurisdictionSlug: Schema.String,
  jurisdictionName: Schema.String,
  title: Schema.String,
  summary: Schema.NullOr(Schema.String),
  sourceUrl: Schema.NullOr(Schema.String),
  publishedAt: Schema.NullOr(Schema.String),
  detectedAt: Schema.String,
  affectedPolicyIds: Schema.Array(Schema.String),
  // The monitor model; optional so an older server still decodes.
  monitorId: Schema.optionalKey(Schema.NullOr(Schema.String)),
  monitorName: Schema.optionalKey(Schema.NullOr(Schema.String)),
  frameworkId: Schema.optionalKey(Schema.NullOr(Schema.String)),
  frameworkName: Schema.optionalKey(Schema.NullOr(Schema.String)),
  kind: Schema.optionalKey(PublicationKindSchema),
  stage: Schema.optionalKey(PublicationStageSchema),
  effectiveAt: Schema.optionalKey(Schema.NullOr(Schema.String)),
  commentDeadlineAt: Schema.optionalKey(Schema.NullOr(Schema.String)),
  applicability: Schema.optionalKey(PublicationApplicabilitySchema),
  applicabilityReason: Schema.optionalKey(Schema.NullOr(Schema.String)),
  applicabilityDecidedByEmail: Schema.optionalKey(Schema.NullOr(Schema.String)),
  applicabilityDecidedAt: Schema.optionalKey(Schema.NullOr(Schema.String)),
  openFindings: Schema.optionalKey(Schema.Number),
  totalFindings: Schema.optionalKey(Schema.Number),
});
export type RegulatoryUpdate = typeof RegulatoryUpdateSchema.Type;

const RegulatoryUpdateListSchema = Schema.Struct({
  updates: Schema.Array(RegulatoryUpdateSchema),
  monitoringEnabled: Schema.Boolean,
  lastCheckedAt: Schema.NullOr(Schema.String),
});
export type RegulatoryUpdateSnapshot = typeof RegulatoryUpdateListSchema.Type;

const decodePolicies = Schema.decodeUnknownEffect(PolicyListSchema);
const decodeUpdates = Schema.decodeUnknownEffect(RegulatoryUpdateListSchema);

const policiesUrl = (organizationId: string, extra?: Record<string, string>) =>
  resolvePrimaryEnvironmentHttpUrl("/api/amoreg/policies", { organizationId, ...extra });

const updatesUrl = (organizationId: string) =>
  resolvePrimaryEnvironmentHttpUrl("/api/amoreg/regulatory-updates", { organizationId });
const publicationsUrl = (organizationId: string, changeId: string) =>
  resolvePrimaryEnvironmentHttpUrl("/api/amoreg/regulation/publications", { organizationId, changeId });

/** Fast enough to feel live, slow enough not to hammer the API. */
const PROCESSING_POLL_MS = 2_000;

const policiesAtom = Atom.family((organizationId: string) =>
  complianceQueryAtom(organizationId,
      organizationId.length === 0
        ? Effect.succeed(null)
        : fetchJson(
            {
              proxyUrl: policiesUrl(organizationId),
              apiPath: domainApiReadPath("policies", { organizationId }),
            },
            decodePolicies,
          ),
    )
    .pipe(Atom.withLabel(`web-amoreg:policies:${organizationId}`)),
);

const updatesAtom = Atom.family((organizationId: string) =>
  complianceQueryAtom(organizationId,
      organizationId.length === 0
        ? Effect.succeed(null)
        : fetchJson(
            {
              proxyUrl: updatesUrl(organizationId),
              apiPath: domainApiReadPath("regulatory-updates", { organizationId }),
            },
            decodeUpdates,
          ),
    )
    .pipe(Atom.withLabel(`web-amoreg:regulatory-updates:${organizationId}`)),
);

/** An upload either fails with a message or yields the thing it made. */
export type UploadOutcome<T> = { readonly error: string; readonly value?: undefined } | { readonly error: null; readonly value: T };

const decodePolicy = Schema.decodeUnknownSync(PolicySchema);

export interface PolicyUploadInput {
  readonly file: File;
  readonly jurisdictionSlugs: ReadonlyArray<string>;
  /** File it into the knowledge base rather than as a policy (see `isKnowledgeDocument`). */
  readonly reference?: boolean;
}

export interface PoliciesView {
  readonly snapshot: PolicyListSnapshot | null;
  readonly unavailableReason: OrgProfileUnavailableReason | null;
  readonly available: boolean;
  readonly isPending: boolean;
  readonly refresh: () => void;
  /** Upload a policy; resolves to an error message, or null on success. */
  readonly upload: (input: PolicyUploadInput) => Promise<string | null>;
  /** The same upload, handing back the document it created — for a flow that must name it. */
  readonly uploadDocument: (input: PolicyUploadInput) => Promise<UploadOutcome<Policy>>;
  readonly remove: (policyId: string) => Promise<string | null>;
  /** Replace a policy's jurisdictions (admin); resolves to an error or null. */
  readonly setJurisdictions: (
    policyId: string,
    jurisdictionSlugs: ReadonlyArray<string>,
  ) => Promise<string | null>;
  /**
   * Upload a new version of a policy's file; the previous file stays in the
   * policy's history. Resolves to an error message, or null on success.
   */
  readonly replaceFile: (
    policyId: string,
    file: File,
    note: string | null,
  ) => Promise<string | null>;
  /** The same upload, handing back the version number it produced. */
  readonly uploadVersion: (
    policyId: string,
    file: File,
    note: string | null,
    expectedVersion?: number,
  ) => Promise<UploadOutcome<number>>;
  /**
   * Re-run ingestion on the current file (admin) — no new version. For a
   * document whose processing failed or whose index needs rebuilding.
   * Resolves to an error message, or null once the run has started.
   */
  readonly reprocess: (policyId: string) => Promise<string | null>;
  /** Opens the server-proxied download, never the storage URL; a version picks a superseded file. */
  readonly download: (policyId: string, version?: number) => void;
  /** The same guarded bytes, served inline for an embedded viewer; null without an org. */
  readonly previewUrl: (policyId: string, version?: number) => string | null;
}

/** Base64 for the JSON contract; chunked so a 10 MB file does not blow the call stack. */
export const fileToBase64 = async (file: File): Promise<string> => {
  const bytes = new Uint8Array(await file.arrayBuffer());
  let binary = "";
  const chunk = 0x8000;
  for (let offset = 0; offset < bytes.length; offset += chunk) {
    binary += String.fromCharCode(...bytes.subarray(offset, offset + chunk));
  }
  return btoa(binary);
};

const readState = <A>(
  result: AsyncResult.AsyncResult<A | { unavailable: OrgProfileUnavailableReason } | null, unknown>,
) => {
  const value = AsyncResult.value(result);
  const data = value._tag === "Some" ? value.value : null;
  const unavailable =
    data !== null && typeof data === "object" && "unavailable" in data
      ? (data.unavailable as OrgProfileUnavailableReason)
      : result._tag === "Failure"
        ? ("error" as const)
        : null;
  return {
    snapshot: unavailable === null ? ((data ?? null) as A | null) : null,
    unavailable,
    isPending: result.waiting,
  };
};

export function usePolicies(organizationId: string | null): PoliciesView {
  const atom = useMemo(() => policiesAtom(organizationId ?? ""), [organizationId]);
  const result = useAtomValue(atom);
  const state = readState<PolicyListSnapshot>(result);

  const refresh = useCallback(() => {
    appAtomRegistry.refresh(atom);
  }, [atom]);

  const uploadDocument = useCallback(
    async (input: PolicyUploadInput): Promise<UploadOutcome<Policy>> => {
      if (!organizationId) return { error: "No organization selected." };
      const failure = input.reference === true ? "The file could not be uploaded." : "The policy could not be uploaded.";
      try {
        // Base64 keeps the domain API's contract schema-typed end to end; the
        // file never goes to storage from the browser, so the API stays the
        // only writer.
        const response = await fetch(policiesUrl(organizationId), {
          method: "POST",
          credentials: "include",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({
            filename: input.file.name,
            contentBase64: await fileToBase64(input.file),
            jurisdictionSlugs: [...input.jurisdictionSlugs],
            ...(input.reference === true ? { reference: true } : {}),
          }),
        });
        if (!response.ok) {
          const body = await response.text().catch(() => "");
          return { error: messageFromBody(body, failure) };
        }
        const policy = decodePolicy(await response.json());
        refresh();
        return { error: null, value: policy };
      } catch {
        return { error: failure };
      }
    },
    [organizationId, refresh],
  );
  const upload = useCallback(
    async (input: PolicyUploadInput): Promise<string | null> => (await uploadDocument(input)).error,
    [uploadDocument],
  );

  const remove = useCallback(
    async (policyId: string): Promise<string | null> => {
      if (!organizationId) return "No organization selected.";
      try {
        const response = await fetch(policiesUrl(organizationId, { policyId }), {
          method: "DELETE",
          credentials: "include",
        });
        if (!response.ok) {
          const body = await response.text().catch(() => "");
          return messageFromBody(body, "The policy could not be removed.");
        }
        refresh();
        return null;
      } catch {
        return "The policy could not be removed.";
      }
    },
    [organizationId, refresh],
  );

  const setJurisdictions = useCallback(
    async (policyId: string, jurisdictionSlugs: ReadonlyArray<string>): Promise<string | null> => {
      if (!organizationId) return "No organization selected.";
      try {
        const response = await fetch(
          resolvePrimaryEnvironmentHttpUrl("/api/amoreg/policies/jurisdictions", {
            organizationId,
            policyId,
          }),
          {
            method: "POST",
            credentials: "include",
            headers: { "content-type": "application/json" },
            body: JSON.stringify({ jurisdictionSlugs: [...jurisdictionSlugs] }),
          },
        );
        if (!response.ok) {
          const body = await response.text().catch(() => "");
          return messageFromBody(body, "The jurisdictions could not be saved.");
        }
        refresh();
        return null;
      } catch {
        return "The jurisdictions could not be saved.";
      }
    },
    [organizationId, refresh],
  );

  const uploadVersion = useCallback(
    async (policyId: string, file: File, note: string | null, expectedVersion?: number): Promise<UploadOutcome<number>> => {
      if (!organizationId) return { error: "No organization selected." };
      try {
        const response = await fetch(
          resolvePrimaryEnvironmentHttpUrl("/api/amoreg/policies/file", {
            organizationId,
            policyId,
          }),
          {
            method: "POST",
            credentials: "include",
            headers: { "content-type": "application/json" },
            body: JSON.stringify({
              filename: file.name,
              contentBase64: await fileToBase64(file),
              note,
              ...(expectedVersion === undefined ? {} : { expectedVersion }),
            } satisfies typeof PolicyFileUpload.Type),
          },
        );
        if (!response.ok) {
          const body = await response.text().catch(() => "");
          return { error: messageFromBody(body, "The new version could not be uploaded.") };
        }
        const policy = decodePolicy(await response.json());
        refresh();
        return { error: null, value: policyVersion(policy) };
      } catch {
        return { error: "The new version could not be uploaded." };
      }
    },
    [organizationId, refresh],
  );
  const replaceFile = useCallback(
    async (policyId: string, file: File, note: string | null): Promise<string | null> =>
      (await uploadVersion(policyId, file, note)).error,
    [uploadVersion],
  );

  const reprocess = useCallback(
    async (policyId: string): Promise<string | null> => {
      if (!organizationId) return "No organization selected.";
      try {
        const response = await fetch(
          resolvePrimaryEnvironmentHttpUrl("/api/amoreg/policies/reprocess", {
            organizationId,
            policyId,
          }),
          { method: "POST", credentials: "include" },
        );
        if (!response.ok) {
          const body = await response.text().catch(() => "");
          return messageFromBody(body, "Processing could not be restarted.");
        }
        refresh();
        return null;
      } catch {
        return "Processing could not be restarted.";
      }
    },
    [organizationId, refresh],
  );

  const download = useCallback(
    (policyId: string, version?: number) => {
      if (!organizationId) return;
      window.open(
        resolvePrimaryEnvironmentHttpUrl("/api/amoreg/policies/download", {
          organizationId,
          policyId,
          ...(version === undefined ? {} : { version: String(version) }),
        }),
        "_blank",
        "noopener",
      );
    },
    [organizationId],
  );

  const previewUrl = useCallback(
    (policyId: string, version?: number) =>
      organizationId
        ? resolvePrimaryEnvironmentHttpUrl("/api/amoreg/policies/download", {
            organizationId,
            policyId,
            inline: "1",
            ...(version === undefined ? {} : { version: String(version) }),
          })
        : null,
    [organizationId],
  );

  return {
    snapshot: state.snapshot,
    unavailableReason: state.unavailable,
    available: state.unavailable === null,
    isPending: state.isPending,
    refresh,
    upload,
    remove,
    setJurisdictions,
    replaceFile,
    uploadVersion,
    uploadDocument,
    reprocess,
    download,
    previewUrl,
  };
}

export interface RegulatoryUpdatesView {
  /** A person's word on whether a publication touches this organization; null on success. */
  readonly decideApplicability: (
    changeId: string,
    applicability: PublicationApplicability,
    reason: string | null,
  ) => Promise<string | null>;
  readonly snapshot: RegulatoryUpdateSnapshot | null;
  readonly unavailableReason: OrgProfileUnavailableReason | null;
  readonly available: boolean;
  readonly isPending: boolean;
  readonly refresh: () => void;
}

export function useRegulatoryUpdates(organizationId: string | null): RegulatoryUpdatesView {
  const atom = useMemo(() => updatesAtom(organizationId ?? ""), [organizationId]);
  const result = useAtomValue(atom);
  const state = readState<RegulatoryUpdateSnapshot>(result);
  const refresh = useCallback(() => {
    appAtomRegistry.refresh(atom);
  }, [atom]);
  const decideApplicability = useCallback(
    async (
      changeId: string,
      applicability: PublicationApplicability,
      reason: string | null,
    ): Promise<string | null> => {
      if (!organizationId) return "No organization selected.";
      try {
        const response = await fetch(publicationsUrl(organizationId, changeId), {
          method: "POST",
          credentials: "include",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ applicability, reason }),
        });
        if (!response.ok) {
          if (response.status === 403) return "Only organization admins can decide applicability.";
          const body = await response.text().catch(() => "");
          return messageFromBody(body, "The decision could not be recorded.");
        }
        refresh();
        return null;
      } catch {
        return "The decision could not be recorded.";
      }
    },
    [organizationId, refresh],
  );

  return {
    snapshot: state.snapshot,
    unavailableReason: state.unavailable,
    available: state.unavailable === null,
    isPending: state.isPending,
    refresh,
    decideApplicability,
  };
}

/** The API answers with `{message}`; fall back when it does not. */
export function messageFromBody(body: string, fallback: string): string {
  try {
    const parsed = JSON.parse(body) as { message?: string };
    return parsed.message ?? fallback;
  } catch {
    return fallback;
  }
}

// ── live ingestion ─────────────────────────────────────────────────────────

const ProcessingLogSchema = Schema.Struct({
  timestamp: Schema.String,
  level: Schema.String,
  message: Schema.String,
});

const ProcessingBatchSchema = Schema.Struct({
  batchNumber: Schema.Number,
  totalBatches: Schema.Number,
  startPage: Schema.Number,
  endPage: Schema.Number,
  status: Schema.String,
});

const ProcessingStatsSchema = Schema.Struct({
  totalSections: Schema.optional(Schema.Number),
  chunksProcessed: Schema.optional(Schema.Number),
  imagesAnalyzed: Schema.optional(Schema.Number),
  tablesAnalyzed: Schema.optional(Schema.Number),
  graphsAnalyzed: Schema.optional(Schema.Number),
  coloredTextSegments: Schema.optional(Schema.Number),
  batchesTotal: Schema.optional(Schema.Number),
  batchesProcessed: Schema.optional(Schema.Number),
  conversionTimeMs: Schema.optional(Schema.Number),
  analysisTimeMs: Schema.optional(Schema.Number),
  chunkingTimeMs: Schema.optional(Schema.Number),
  embeddingTimeMs: Schema.optional(Schema.Number),
  validationErrors: Schema.optional(Schema.Array(Schema.String)),
});
export type PolicyProcessingStats = typeof ProcessingStatsSchema.Type;

const ProcessingStateSchema = Schema.Struct({
  jobId: Schema.NullOr(Schema.String),
  status: Schema.NullOr(Schema.String),
  progress: Schema.Number,
  currentStep: Schema.NullOr(Schema.String),
  error: Schema.NullOr(Schema.String),
  logs: Schema.Array(ProcessingLogSchema),
  batches: Schema.Array(ProcessingBatchSchema),
  stats: Schema.NullOr(ProcessingStatsSchema),
  createdAt: Schema.NullOr(Schema.String),
  startedAt: Schema.NullOr(Schema.String),
  completedAt: Schema.NullOr(Schema.String),
  active: Schema.Boolean,
});
export type PolicyProcessingState = typeof ProcessingStateSchema.Type;

const PolicyJobSchema = Schema.Struct({
  id: Schema.String,
  status: Schema.String,
  progress: Schema.Number,
  createdAt: Schema.String,
  startedAt: Schema.NullOr(Schema.String),
  completedAt: Schema.NullOr(Schema.String),
});
export type PolicyJob = typeof PolicyJobSchema.Type;

const PolicyJobListSchema = Schema.Struct({ jobs: Schema.Array(PolicyJobSchema) });

const PolicyChunkSchema = Schema.Struct({
  id: Schema.String,
  sectionType: Schema.String,
  sectionPath: Schema.String,
  title: Schema.NullOr(Schema.String),
  abstract: Schema.NullOr(Schema.String),
  content: Schema.String,
  startPage: Schema.NullOr(Schema.Number),
  endPage: Schema.NullOr(Schema.Number),
  tokenCount: Schema.NullOr(Schema.Number),
  vectorCount: Schema.Number,
});
export type PolicyChunk = typeof PolicyChunkSchema.Type;

const PolicyChunkListSchema = Schema.Struct({
  chunks: Schema.Array(PolicyChunkSchema),
  truncated: Schema.Boolean,
  /** "unavailable" = no vector store here, which is not the same as empty. */
  source: Schema.Literals(["vector-store", "unavailable"]),
});

const decodeProcessing = Schema.decodeUnknownEffect(ProcessingStateSchema);
const decodeJobs = Schema.decodeUnknownEffect(PolicyJobListSchema);
const decodeChunks = Schema.decodeUnknownEffect(PolicyChunkListSchema);

/**
 * Live ingestion state for one document.
 *
 * Polled rather than streamed: the worker appends to the job's console log as
 * it chunks and embeds, so a short interval gives a live feed without holding
 * a socket open through the proxy. Polling **stops** as soon as the job
 * reaches a terminal state — a finished document must not keep a timer alive.
 */
export function usePolicyProcessing(
  organizationId: string | null,
  policyId: string | null,
  /** A specific run from the history; omitted means the latest. */
  jobId?: string | null,
): { state: PolicyProcessingState | null; isPending: boolean } {
  const [state, setState] = useState<PolicyProcessingState | null>(null);
  const [isPending, setIsPending] = useState(false);

  useEffect(() => {
    if (!organizationId || !policyId) {
      setState(null);
      return;
    }
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const read = async () => {
      setIsPending(true);
      const url = resolvePrimaryEnvironmentHttpUrl("/api/amoreg/policies/processing", {
        organizationId,
        policyId,
        ...(jobId ? { jobId } : {}),
      });
      const next = await Effect.runPromise(
        fetchJson(url, decodeProcessing).pipe(
          Effect.orElseSucceed(() => ({ unavailable: "error" as const })),
        ),
      );
      if (cancelled) return;
      setIsPending(false);
      if (next !== null && typeof next === "object" && "unavailable" in next) {
        return;
      }
      setState(next);
      if (next?.active) {
        timer = setTimeout(() => void read(), PROCESSING_POLL_MS);
      }
    };

    void read();
    return () => {
      cancelled = true;
      if (timer !== null) clearTimeout(timer);
    };
  }, [organizationId, policyId, jobId]);

  return { state, isPending };
}

/**
 * A document's processing history, fetched only once asked for.
 *
 * Lazy on purpose: the run list answers "which run produced this?", a
 * question nobody asks until they open the picker, and a document page must
 * not spend a request per view on it.
 */
export function usePolicyJobs(
  organizationId: string | null,
  policyId: string | null,
  enabled: boolean,
): { jobs: ReadonlyArray<PolicyJob>; isPending: boolean } {
  const [jobs, setJobs] = useState<ReadonlyArray<PolicyJob>>([]);
  const [isPending, setIsPending] = useState(false);

  useEffect(() => {
    if (!enabled || !organizationId || !policyId) return;
    let cancelled = false;
    setIsPending(true);
    const url = resolvePrimaryEnvironmentHttpUrl("/api/amoreg/policies/jobs", {
      organizationId,
      policyId,
    });
    void Effect.runPromise(fetchJson(url, decodeJobs)).then((result) => {
      if (cancelled) return;
      setIsPending(false);
      if (result !== null && typeof result === "object" && "unavailable" in result) return;
      setJobs(result.jobs);
    });
    return () => {
      cancelled = true;
    };
  }, [enabled, organizationId, policyId]);

  return { jobs, isPending };
}

export interface PolicyChunksView {
  readonly chunks: ReadonlyArray<PolicyChunk>;
  readonly truncated: boolean;
  readonly isPending: boolean;
  /** The read failed, or this deployment stores no vectors at all. */
  readonly unavailable: boolean;
}

/**
 * The sections a document was split into. Also lazy — the payload carries the
 * embedded text, so it is fetched when the reader opens it, not on page load.
 */
export function usePolicyChunks(
  organizationId: string | null,
  policyId: string | null,
  enabled: boolean,
): PolicyChunksView {
  const [chunks, setChunks] = useState<ReadonlyArray<PolicyChunk>>([]);
  const [truncated, setTruncated] = useState(false);
  const [unavailable, setUnavailable] = useState(false);
  const [isPending, setIsPending] = useState(false);

  useEffect(() => {
    if (!enabled || !organizationId || !policyId) return;
    let cancelled = false;
    setIsPending(true);
    const url = resolvePrimaryEnvironmentHttpUrl("/api/amoreg/policies/chunks", {
      organizationId,
      policyId,
    });
    void Effect.runPromise(fetchJson(url, decodeChunks)).then((result) => {
      if (cancelled) return;
      setIsPending(false);
      if (result !== null && typeof result === "object" && "unavailable" in result) {
        setUnavailable(true);
        return;
      }
      setUnavailable(result.source === "unavailable");
      setChunks(result.chunks);
      setTruncated(result.truncated);
    });
    return () => {
      cancelled = true;
    };
  }, [enabled, organizationId, policyId]);

  return { chunks, truncated, isPending, unavailable };
}

/** Warm the overview's document and regulatory-update reads without a second cache. */
export function complianceOverviewPolicyAtoms(
  organizationId: string,
): ReadonlyArray<Atom.Atom<AsyncResult.AsyncResult<unknown, unknown>>> {
  return [policiesAtom(organizationId), updatesAtom(organizationId)];
}
